import {
  Box,
  Button,
  Cog6ToothIcon,
  DeleteConfirmButton,
  Divider,
  Drawer,
  DrawerProps,
  Flex,
  HelpHover,
  InputContainer,
  LocationAutocomplete,
  Menu,
  NoSymbolIcon,
  QrCodeIcon,
  QueueListIcon,
  RadioCards,
  RangeDateSelect,
  ServerStackIcon,
  Text,
  TextField,
  UserSelect,
  toaster,
} from 'components'
import { SingleUseMediaInput } from 'components/media'
import { useAlert, useCurrentUser, useWorkspace } from 'components/providers'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { isEqual } from 'lodash-es'
import { useEffect, useRef, useState } from 'react'
import { DBProps, EventRegistrationMode, IEvent, PartialBy, QRCodeConfiguration } from 'types'
import { directUploadImageToStorage, removeEmpty, updateDoc, useBlockResourceCreation } from 'utils'
import { useTemplates } from 'utils/templateUtils'
import { useGoToWorkspace, usePermission } from 'utils/workspaceUtils'
import defaultImage from '../../../assets/event_tile.png'
import { getEventImagePath, isQrCodeConfigurationValid } from '../../../utils/eventUtils'
import DatabaseLink from './DatabaseLink'
import EventLeadDefaults from './EventLeadDefaults'
import QrLink from './QrLink'
import SessionOptions from './SessionOptions'

dayjs.extend(isBetween)

export interface Props extends Omit<DrawerProps, 'children'> {
  /** if id is passed, then update existing document */
  event?: Event
  open: boolean
  onClose: (event?: Event) => void
  onDelete?: () => Promise<void>
  refetchImage?: () => void
}

type Event = PartialBy<IEvent, keyof DBProps | 'id'>
type Draft = PartialBy<Event, 'location' | 'title'>

export default function EventManage({ open, onClose, onDelete, event, refetchImage }: Props) {
  const { confirm } = useAlert()
  const user = useCurrentUser()
  const { subscription, workspace } = useWorkspace()
  const blockEventCreation = useBlockResourceCreation('events')
  const permission = usePermission()
  const { templates } = useTemplates()
  const goToWorkspace = useGoToWorkspace()
  const goBack = () => goToWorkspace(workspace?.id, {})

  const [draft, setDraft] = useState<Draft>()
  const [originalState, setOriginalState] = useState<Draft>()
  const [errorFields, setErrorFields] = useState<string[]>([])

  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open || !user || !workspace || !templates) return

    const inWorkspace = workspace.userIds?.includes(user.id)
    const firstAdminId = Object.keys(workspace.userById).find(
      id => workspace.userById[id].permission === 'admin'
    )
    const firstEditorId = Object.keys(workspace.userById).find(
      id => workspace.userById[id].permission === 'editor'
    )
    const managerId = inWorkspace ? user.id : firstAdminId || firstEditorId || user.id
    const isEntevateAdmin = managerId === user.id && !inWorkspace
    const body = event ?? {
      endTime: 0,
      leadCount: 0,
      managerId,
      memberIds: isEntevateAdmin ? [] : [managerId],
      moments: [],
      registrationMode: 'input',
      sessionCount: 0,
      startTime: 0,
      workspaceId: workspace.id,
      ...(templates.length === 1 && { template: { id: templates[0].id } }),
    }
    setDraft(body)
    setOriginalState(body)
    setErrorFields([])
  }, [open, user, workspace, event, templates])

  if (!draft) return null

  const {
    endTime,
    hasImage,
    id,
    location,
    managerId,
    memberIds = [],
    qrCode,
    registrationMode,
    sessionCount,
    startTime,
    title = '',
    url = '',
  } = draft

  const allowEdit = permission.event === 'admin' || user?.id === managerId
  const allowStartDateEdit = allowEdit && (!startTime || !sessionCount)
  const isUpcoming = new Date().getTime() < startTime
  const isDirty = !isEqual(draft, originalState)

  function pullImageFromUrl() {
    if (id && !hasImage) {
      const fullFilePath = `Workspaces/${workspace!.id}/${getEventImagePath(id)}`
      directUploadImageToStorage(user?.id!, url, fullFilePath, id)
    }
  }

  async function updateEvent(body: Partial<IEvent>) {
    setDraft({ ...draft!, ...body })
  }

  async function handleDelete() {
    await onDelete?.().catch(() =>
      toaster.create({ type: 'error', description: 'Failed to delete event' })
    )
    goBack()
  }

  function handleDone() {
    if (!isDirty || !draft) return onClose()

    let errors: string[] = []
    if (!title) errors.push('title')
    if (!location) errors.push('location')
    if (!startTime) errors.push('startTime')

    if (registrationMode === 'qr-code') {
      const isQrCodeComplete = isQrCodeConfigurationValid(qrCode)

      if (!isQrCodeComplete) {
        errors.push('qrCode')
        toaster.create({
          type: 'error',
          title: 'Incomplete QR Code Configuration',
          description: 'Please complete and authenticate the QR code configuration before saving.',
        })
      }
    }

    if (errors.length) return setErrorFields(errors)

    const cleaned = removeEmpty({ ...(draft as Event), title: title.trim(), url: url?.trim() })
    onClose(cleaned)
  }

  async function handleCancel() {
    if (isDirty) {
      const response = await confirm('Are you sure you want to discard changes?', {
        cancelText: 'Cancel',
        okText: 'Discard',
      })
      if (!response) return
    }
    onClose()
  }

  function addErrorField(field: string) {
    if (!errorFields.includes(field)) setErrorFields([...errorFields, field])
  }

  function removeErrorField(field: string) {
    if (errorFields.includes(field)) setErrorFields(errorFields.filter(f => f !== field))
  }

  async function confirmManagerChange(newManagerId: string) {
    if (user!.id === managerId && permission.event !== 'admin') {
      const response = await confirm(
        'You will no longer be able to assign/change the event manager if you click Confirm. Please reach out to your workspace admin to be added back.',
        { cancelText: 'Cancel', okText: 'Confirm' }
      )
      if (!response) return false
    }

    updateEvent({ managerId: newManagerId })
  }

  async function regModeChange(value: EventRegistrationMode) {
    if (value !== 'qr-code' && qrCode?.isValid) {
      const response = await confirm(
        'Changing registration mode will reset the current registration settings. Are you sure you want to proceed?',
        { okText: 'Proceed', cancelText: 'Cancel' }
      )
      if (!response) return
    }

    updateEvent({
      registrationMode: value,
      qrCode: value === 'qr-code' ? defaultQrCode : undefined,
    })
  }

  return (
    <Drawer
      onClose={handleCancel}
      open={open}
      header={`${event ? 'Manage' : 'Create'} Event`}
      headerActions={
        Boolean(onDelete) &&
        allowEdit &&
        sessionCount === 0 && (
          <Menu
            menuProps={{ positioning: { placement: 'bottom-start' } }}
            variant="ghost"
            buttonLabel="Actions"
            options={[
              {
                type: 'custom',
                label: (
                  <DeleteConfirmButton onDelete={handleDelete} width="100%" borderRadius="none" />
                ),
              },
            ]}
          />
        )
      }
      headerIcon={<Cog6ToothIcon fontSize="20px" />}
      footer={
        <>
          {isDirty && (
            <Button variant="ghost" onClick={handleCancel}>
              Cancel
            </Button>
          )}
          <Button variant="solid" palette="primary" onClick={handleDone} width="70px">
            {isDirty ? 'Save' : 'Done'}
          </Button>
        </>
      }
    >
      <Flex direction="column" gap={6} ref={contentRef}>
        <TextField
          label="Name"
          required
          value={title}
          placeholder="Please enter event name"
          onChange={e => {
            updateEvent({ title: e.target.value })
            removeErrorField('title')
          }}
          autoFocus
          onBlur={() => !title && addErrorField('title')}
          isError={errorFields.includes('title')}
          helperText={errorFields.includes('title') ? 'Please enter event name' : undefined}
        />

        <Box position="relative">
          <RangeDateSelect
            label="Event Dates"
            disabled={!allowStartDateEdit}
            required
            menuProps={{
              menuProps: { positioning: { placement: 'bottom-start' } },
              portal: { container: contentRef },
            }}
            selected={startTime ? { from: new Date(startTime), to: new Date(endTime) } : undefined}
            onSelect={range => {
              if (!range?.from) return

              const { from, to } = range
              const resetUTC = subscription?.limitsResetDate
              if (resetUTC) {
                const resetDate = dayjs(resetUTC)
                const startDate = resetDate.subtract(1, 'year')
                const isCurrentYear = dayjs(from).isBetween(startDate, resetDate)
                if (isCurrentYear && blockEventCreation()) return
              }
              updateEvent({ startTime: from, endTime: to ?? from })
              removeErrorField('startTime')
            }}
            pickerProps={{ max: 7 }}
          />
          <Text textStyle="bodyXSmall" color="red.500" position="absolute" top="100%" opacity={0.6}>
            {errorFields.includes('startTime') && 'Please select a start date'}
          </Text>
        </Box>

        <LocationAutocomplete
          label="Event Location"
          required
          isError={errorFields.includes('location')}
          helperText={errorFields.includes('location') ? 'Please enter event location' : ''}
          value={location}
          onChange={location => {
            updateEvent({ location })
            if (!location) addErrorField('location')
            else removeErrorField('location')
          }}
          popoverProps={{ portal: { container: contentRef } }}
        />

        {allowEdit && (
          <>
            <UserSelect
              label="Event Manager"
              value={managerId}
              onChange={confirmManagerChange}
              permissionsAllowed={['admin', 'editor']}
            />
            <UserSelect
              label={
                <Flex align="center" gap={2}>
                  <Text textStyle={'bodySmall'} opacity={0.7}>
                    Event Members
                  </Text>
                  <HelpHover
                    iconProps={{ size: 'sm' }}
                    positioning={{ placement: 'right' }}
                    text={`Members ${isUpcoming ? 'who are attending' : 'who attended'} the event`}
                  />
                </Flex>
              }
              isMulti
              userDetail={{ [managerId]: { hideFromValue: true, title: 'Event Manager' } }}
              value={memberIds}
              onChange={memberIds => updateEvent({ memberIds })}
            />
          </>
        )}

        <TextField
          label="Event URL"
          id="eventURL"
          value={url}
          placeholder="Please enter event URL"
          onChange={e => updateEvent({ url: e.target.value })}
          onBlur={pullImageFromUrl}
        />

        <EventLeadDefaults
          allowEdit={allowEdit}
          leadDefaults={draft.leadDefaults}
          onChange={leadDefaults => updateEvent({ leadDefaults })}
        />

        {id && (
          <SingleUseMediaInput
            type="image"
            defaultImage={defaultImage}
            siteUrl={url}
            filePath={getEventImagePath(id)}
            noFile={!hasImage}
            onClose={async action => {
              if (action === 'save' && !hasImage) {
                await updateDoc(user!.id, 'events', { id, hasImage: true })
              } else if (action === 'delete' && hasImage) {
                await updateDoc(user!.id, 'events', { id, hasImage: false })
              }
              refetchImage?.()
            }}
          />
        )}
        <Divider />

        <InputContainer label="Registration Mode">
          <RadioCards
            value={registrationMode || 'none'}
            options={registrationOptions}
            onChange={regModeChange}
          />
        </InputContainer>

        {registrationMode === 'database' && <DatabaseLink />}

        {registrationMode === 'qr-code' && (
          <QrLink
            eventTitle={title}
            qrCode={qrCode}
            onProviderChange={value => {
              if (!qrCode) return
              const updatedQRCode = {
                ...qrCode,
                type: value == 'not-listed' ? undefined : value,
              } as QRCodeConfiguration
              updateEvent({ qrCode: updatedQRCode })
            }}
            onConfigurationChange={value => {
              if (!qrCode) return
              const updatedQRCode = {
                ...qrCode,
                ...value,
                isValid: value.isValid || false,
                leadMapping: value.leadMapping || null,
              } as QRCodeConfiguration
              updateEvent({ qrCode: updatedQRCode })
            }}
          />
        )}

        <SessionOptions event={draft} onUpdate={updateEvent} />
        <Box pt={5} />
      </Flex>
    </Drawer>
  )
}

const registrationOptions: { value: EventRegistrationMode; label: string; icon: JSX.Element }[] = [
  { value: 'input', label: 'Form', icon: <QueueListIcon /> },
  { value: 'database', label: 'Database', icon: <ServerStackIcon /> },
  { value: 'qr-code', label: 'QR Code', icon: <QrCodeIcon /> },
  { value: 'none', label: 'None', icon: <NoSymbolIcon /> },
]

const defaultQrCode: QRCodeConfiguration = {
  authKey: '',
  eventCode: '',
  exhibitorId: '',
  type: 'xpressleads',
  isValid: false,
  leadMapping: null,
}
